import { History } from "lucide-react";
import clsx from "clsx";
import {
  ILabReportInterpretationEMR,
  ILabReportInterpretationPreviousMedicine,
} from "@/api_framework/api_modals/LabReportInterpretation";
import { useLabReportInterpretationEMRUpdateAPI } from "@/app/hooks/api_hooks/LabReportInterpretation/useLabReportInterpretationEMRUpdateAPI";

interface IPreviousMedicationsSectionProps {
  emrId: string;
  emrDetails: ILabReportInterpretationEMR;
  setEmrDetails: (details: Partial<ILabReportInterpretationEMR>) => void;
}

const STATUS_OPTIONS = [
  { isContinue: true, label: "Continue", activeClass: "text-[#00770C]" },
  { isContinue: false, label: "Pause", activeClass: "text-[#ED0101]" },
];

const getDosageSummary = (medicine: ILabReportInterpretationPreviousMedicine) =>
  [
    medicine.dose && `${medicine.dose} ${medicine.dose_unit ?? ""}`.trim(),
    medicine.frequency,
    medicine.timing,
    medicine.duration && `${medicine.duration} ${medicine.duration_unit ?? ""}`.trim(),
  ]
    .filter(Boolean)
    .join(" · ");

const PreviousMedicationsSection = ({ emrId, emrDetails, setEmrDetails }: IPreviousMedicationsSectionProps) => {
  const { execute: updateEMR, loading: saving } = useLabReportInterpretationEMRUpdateAPI();

  const previousMedicines = emrDetails?.previous_medicines ?? [];
  if (!previousMedicines.length) return null;

  const handleStatusChange = async (medicineId: string, isContinue: boolean) => {
    const nextMedicines = previousMedicines.map((medicine) =>
      medicine.medicine_id === medicineId ? { ...medicine, is_continue: isContinue } : medicine,
    );
    if (await updateEMR({ emr_id: emrId, previous_medicines: nextMedicines })) {
      setEmrDetails({ previous_medicines: nextMedicines });
    }
  };

  return (
    <div className="bg-white rounded-xl p-3">
      <h3 className="flex items-center gap-1.5 text-sm px-2 mb-1.5">
        <History className="w-4 h-4" aria-hidden="true" />
        Previous Medications
      </h3>
      <ul className="flex flex-col gap-2">
        {previousMedicines.map((medicine) => {
          const isContinue = medicine.is_continue !== false;
          const dosageSummary = getDosageSummary(medicine);
          return (
            <li
              key={medicine.medicine_id}
              className={clsx(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl border",
                isContinue ? "border-[#B7E4BD] bg-[#F4FFF4]" : "border-[#FBC4C4] bg-[#FFEDED]",
              )}
            >
              <span className="flex-1 min-w-0">
                <span title={medicine.name} className="block text-sm font-semibold text-gray-900 truncate">
                  {medicine.name}
                </span>
                {dosageSummary && <span className="block mt-0.5 text-xs text-gray-500 truncate">{dosageSummary}</span>}
              </span>
              <span className="flex items-center gap-4 shrink-0">
                {STATUS_OPTIONS.map((option) => (
                  <label
                    key={option.label}
                    className={clsx(
                      "inline-flex items-center gap-1.5 text-xs font-semibold cursor-pointer",
                      isContinue === option.isContinue ? option.activeClass : "text-gray-600",
                    )}
                  >
                    <input
                      type="radio"
                      name={`previous-medicine-${medicine.medicine_id}`}
                      checked={isContinue === option.isContinue}
                      disabled={saving}
                      onChange={() => handleStatusChange(medicine.medicine_id, option.isContinue)}
                      className={clsx("h-4 w-4 cursor-pointer", option.activeClass)}
                    />
                    {option.label}
                  </label>
                ))}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PreviousMedicationsSection;
